import { ConfigHelper } from './../../services/config.helper';
import * as _ from 'lodash';

import { TestTakerActions } from '../actions';
import * as Enum from '../../dtos/enums';

export interface ITestTakerState {
    assessmentTest: PgRendererDto.AssessmentTest;
    testlet: PgRendererDto.TestletDefinition;
    itemIndex: number;
    testNavigation: PgRendererDto.TestNavigation;
    preloadNavigation: PgRendererDto.TestNavigation;
    flushPayload: PgRendererDto.TdpTestTakerResponse;
    testTakerInfo: PgRendererDto.TestTakerStationAssignment;
    responses: PgRendererDto.InteractionResponse[];
    timer: PgRendererDto.Timer;
    timerVisibility: boolean;
    testTakerActivitiesCache: PgRendererDto.TestTakerActivity[];
    testletHistory: PgRendererDto.TestletHistory[];
    shouldGoToNextTestlet: boolean;
    assessmentIds: number[];
    highlights: PgRendererDto.HighlightDto[];
    isNavigatingInProgress: Object;
}

const initialState: ITestTakerState = {
    assessmentTest: null,
    testlet: null,
    itemIndex: 0,
    testNavigation: {
        dateAdded: new Date(),
        testPartIndex: 0,
        testletIndex: 0,
        canGoNext: false,
        isEndOfTest: false
    },
    preloadNavigation: {
        dateAdded: new Date(),
        testPartIndex: 0,
        testletIndex: 0,
        canGoNext: false,
        isEndOfTest: false
    },
    flushPayload: null,
    testTakerInfo: null,
    responses: [],
    timer: getInitialTimerValue(),
    timerVisibility: false,
    testTakerActivitiesCache: [],
    testletHistory: [],
    shouldGoToNextTestlet: false,
    assessmentIds: [],
    highlights: [],
    isNavigatingInProgress: {}
};

export const getAssociatedAssessmentTests = (state: ITestTakerState) => state.assessmentIds;
export const getTestTakerInfo = (state: ITestTakerState) => state.testTakerInfo;
export const getResponses = (state: ITestTakerState) => state.responses;
export const getTimer = (state: ITestTakerState) => state.timer;
export const getTestTakerActivitiesCache = (state: ITestTakerState) => state.testTakerActivitiesCache;
export const getTestletHistory = (state: ITestTakerState) => state.testletHistory;
export const getFlushPayload = (state: ITestTakerState) => state.flushPayload;
export const shouldGoToNextTestlet = (state: ITestTakerState) => state.shouldGoToNextTestlet;
export const getAssessmentTest = (state: ITestTakerState) => state.assessmentTest;
export const getTestlet = (state: ITestTakerState) => state.testlet;
export const getItemIndex = (state: ITestTakerState) => state.itemIndex;
export const getAssessmentItem = (state: ITestTakerState) => state.testlet ? state.testlet.AssessmentItems[state.itemIndex] : null;
export const getCurrentTestletId = (state: ITestTakerState) => state.assessmentTest ? parseTestletId(state.assessmentTest.TestParts[state.testNavigation.testPartIndex].AssessmentSectionRefs[state.testNavigation.testletIndex].Href) : null;
export const getNextTestletId = (state: ITestTakerState) => state.assessmentTest ? parseTestletId(state.assessmentTest.TestParts[state.preloadNavigation.testPartIndex].AssessmentSectionRefs[state.preloadNavigation.testletIndex].Href) : null;
export const getTestNavigation = (state: ITestTakerState) => state.testNavigation;
export const getTimerVisibility = (state: ITestTakerState) => state.timerVisibility;
export const getStoredHighlights = (state: ITestTakerState) => state.highlights;
export const getIsNavigatingInProgress = (state: ITestTakerState) =>  state.isNavigatingInProgress;

export function testTakerReducer(state = initialState, action: TestTakerActions.All): ITestTakerState {

    switch (action.type) {
        case TestTakerActions.LOAD_TESTLET_SUCCESS: {
            setExtraTestletProperties(action.payload);
            return Object.assign({}, state, { testlet: action.payload, itemIndex: 0, canGoNext: false });
        }

        case TestTakerActions.SET_ITEM_INDEX:
            return Object.assign({}, state, { itemIndex: action.payload });

        case TestTakerActions.LOAD_ASSESSMENT_TEST_SUCCESS:
            return Object.assign({}, state, {
                assessmentTest: action.payload,
                testlet: null,
                itemIndex: 0,
                canGoNext: false
            });

        case TestTakerActions.TEST_NAVIGATION_NEXT:
            const newObject = {};
            const testNavigation = testNavigationNext(state, action.payload.currentTestPartIndex, action.payload.currentTestletIndex);
            const newState = Object.assign({}, state, {
                testNavigation: testNavigation,
                isNavigatingInProgress: newObject
            });
            const preloadNavigation = testNavigationNext(newState, testNavigation.testPartIndex,  testNavigation.testletIndex);
            newState.preloadNavigation = preloadNavigation;
            return newState;

        case TestTakerActions.TEST_NAVIGATION_PREVIOUS:
            return Object.assign({}, state, {
                testNavigation: testNavigationPrevious(state)
            });

        case TestTakerActions.SET_TEST_NAVIGATION:
            return Object.assign({}, state, {
                testNavigation: action.payload
            });

        case TestTakerActions.SET_CAN_GO_NEXT: {
            const testNavigation = Object.assign({}, state.testNavigation);
            testNavigation.canGoNext = action.payload;
            return Object.assign({}, state, { testNavigation: testNavigation });
        }

        case TestTakerActions.INDICATE_END_OF_TEST: {
            const testNavigation = Object.assign({}, state.testNavigation);
            testNavigation.isEndOfTest = true;
            return Object.assign({}, state, { testNavigation: testNavigation });
        }

        case TestTakerActions.SET_RESPONSE:
            return setResponse(state, action.payload);

        case TestTakerActions.REMOVE_RESPONSE_ITEM:
            return removeResponseItem(state, action.payload.interactionId, action.payload.value);

        case TestTakerActions.CLEAR_RESPONSES:
            return clearResponses(state, action.payload);

        case TestTakerActions.START_TIMER: {
            let timer = getInitialTimerValue();
            timer.timeLimits = action.payload.timeLimits;
            timer.startedAt = new Date().getTime();
            if (ConfigHelper.isTdpMode(true)) {
                timer.startedAt = timer.startedAt - (action.payload.timeAlreadyUsed * 1000);
            }
            //timer.previouslyUsedTimeInSeconds = state.timer.previouslyUsedTimeInSeconds;
            //todo
            timer.previouslyUsedTimeInSeconds = 0;
            timer.speakingPreparationTime = action.payload.speakingPreparationTime;
            timer.speakingRecordingTime = action.payload.speakingRecordingTime;
            timer.timesUpCallback = action.payload.timesUpCallback;
            timer.showTimesUpWarning = action.payload.showTimesUpWarning;
            timer.multiplier = action.payload.multiplier;
            return Object.assign({}, state, {
                timer: timer,
                timerVisibility: action.payload.timerVisibility
            });
        }

        case TestTakerActions.STOP_TIMER: {
            let timer = Object.assign({}, state.timer);
            timer.stoppedAt = new Date().getTime();
            return Object.assign({}, state, {
                timer: timer
            });
        }

        case TestTakerActions.PAUSE_TIMER: {
            let timer = Object.assign({}, state.timer);
            timer.pausedAt = new Date().getTime();
            return Object.assign({}, state, {
                timer: timer
            });
        }

        case TestTakerActions.UNPAUSE_TIMER: {
            let timer = Object.assign({}, state.timer);
            if (timer.pausedAt) {
                timer.previouslyUsedTimeInSeconds -= Math.round((new Date().getTime() - timer.pausedAt) / 1000);
            }
            timer.pausedAt = null;
            return Object.assign({}, state, {
                timer: timer
            });
        }

        case TestTakerActions.SET_TEST_TAKER_INFO:
            return Object.assign({}, state, {
                testTakerInfo: action.payload
            });

        case TestTakerActions.CACHE_TEST_TAKER_ACTIVITIES: {
            const newState = Object.assign({}, state, {
                testTakerActivitiesCache: [...state.testTakerActivitiesCache, ...action.payload],
            });
            const testletRelatedActivities: PgRendererDto.TestletHistory[] = action.payload
                .filter(x => x.ActivityTypeId === Enum.TestTakerActivityTypeEnum.EnterTestlet || x.ActivityTypeId === Enum.TestTakerActivityTypeEnum.LeaveTestlet)
                .map(x => {
                    return {
                        TestletId: x.TestletId,
                        ActivityTypeId: x.ActivityTypeId,
                        DateCreatedUtc: x.DateCreatedUtc
                    };
                });
            if (testletRelatedActivities.length > 0) {
                newState.testletHistory = [...state.testletHistory, ...testletRelatedActivities];
            }
            return newState;
        }

        case TestTakerActions.CLEAR_TEST_TAKER_ACTIVITIES:
            return Object.assign({}, state, {
                testTakerActivitiesCache: []
            });

        case TestTakerActions.SET_FLUSH_PAYLOAD:
            return Object.assign({}, state, {
                flushPayload: action.payload
            });

        case TestTakerActions.FLUSH_PAYLOAD_SUCCESS:
            return Object.assign({}, state, {
                shouldGoToNextTestlet: action.payload
            });

        case TestTakerActions.SHOULD_GO_TO_NEXT_TESTLET:
            return Object.assign({}, state, {
                shouldGoToNextTestlet: action.payload
            });

        case TestTakerActions.SET_TEST_INITIALIZATION_INFO: {
            let testInitializationInfo = action.payload;
            let testNavigation = Object.assign({}, state.testNavigation);
            testNavigation.testPartIndex = testInitializationInfo.TestTakerProgress.CurrentTestPartIndex;
            testNavigation.testletIndex = testInitializationInfo.TestTakerProgress.CurrentTestletIndex;
            testNavigation.isEndOfTest = false;
            let timer = getInitialTimerValue();
            timer.previouslyUsedTimeInSeconds = testInitializationInfo.TestTakerProgress.CurrentTestletUsedSeconds;
            return Object.assign({}, state, {
                testNavigation: testNavigation,
                timer: timer,
                responses: testInitializationInfo.CurrentTestletInteractionResponses
            });
        }

        case TestTakerActions.SET_IPP_PRODUCT_INFO: {
            return Object.assign({}, state, {
                assessmentIds: [...action.payload.AssociatedIppAssessmentTestIds]
            });
        }

        case TestTakerActions.TOGGLE_TIMER_VISIBILITY: {
            return Object.assign({}, state, {
                timerVisibility: action.payload
            });
        }

        case TestTakerActions.SET_STORED_HIGHLIGHTS: {
            var resourceId = action.payload.ResourceId;
            var highlightedText = action.payload.HighlightedText;

            var highlight = state.highlights.find(h => h.ResourceId === resourceId);
                if (highlight) {
                    var existingRemoved = state.highlights.filter(r => r.ResourceId !== resourceId);
                    state.highlights = [...existingRemoved, {
                        ResourceId: resourceId,
                        HighlightedText: highlightedText }];
                } else {
                    let dto: PgRendererDto.HighlightDto = {
                        ResourceId: resourceId,
                        HighlightedText: highlightedText
                    };
                    return Object.assign({}, state, { highlights: state.highlights.concat(dto) });
                }
                return Object.assign({}, state, { highlights: state.highlights });
            }

        case TestTakerActions.RESUME_TEST: {
            const timer = Object.assign({}, state.timer);
            timer.previouslyUsedTimeInSeconds = 0;
            timer.pausedAt = null;
            timer.startedAt = new Date().getTime();
            return Object.assign({}, state, {
                itemIndex: 0,
                timer: timer
            });
        }

        default:
            return state;
    }
}

function getInitialTimerValue(): PgRendererDto.Timer {
    return {
        timeLimits: { MinTime: null, MaxTime: null },
        startedAt: null,
        stoppedAt: null,
        pausedAt: null,
        previouslyUsedTimeInSeconds: 0,
        speakingPreparationTime: null,
        speakingRecordingTime: null,
        timesUpCallback: null,
        showTimesUpWarning: false,
        multiplier: 1,
    };
}

function setResponse(state: ITestTakerState, response: PgRendererDto.InteractionResponse): ITestTakerState {
    var responses = [...state.responses.filter(r => r.InteractionId != response.InteractionId), response];
    return Object.assign({}, state, { responses });
}

function removeResponseItem(state: ITestTakerState, interactionId: number, value: string): ITestTakerState {
    var response = state.responses.find(r => r.InteractionId == interactionId);
    if (response) {
        var responses = state.responses.filter(r => r.InteractionId != interactionId);
        var responseItems = response.Items.filter(i => i.Value != value);
        responses = [...responses, {
            InteractionId: interactionId,
            Items: responseItems }];
        return Object.assign({}, state, { responses });
    } else {
        return Object.assign({}, state);
    }
}

function clearResponses(state: ITestTakerState, interactionId: number): ITestTakerState {
    let responses = state.responses.filter(r => r.InteractionId != interactionId);
    return Object.assign({}, state, { responses });
}

function parseTestletId(href: string): number {
    var matched = /testlet(\d+).xml/i.exec(href);
    if (matched) {
        return parseInt(matched[1]);
    }
    return null;
}

function testNavigationNext(state: ITestTakerState, currentTestPartIndex: number, currentTestletIndex: number): PgRendererDto.TestNavigation {
    let testNavigation = Object.assign({}, state.testNavigation);
    let ignoreNavigationAction: boolean = testNavigation.testPartIndex != currentTestPartIndex && testNavigation.testletIndex != currentTestletIndex;

    if (!ignoreNavigationAction && state.assessmentTest) {
        testNavigation.dateAdded = new Date();
        let testPart = state.assessmentTest.TestParts[testNavigation.testPartIndex];
        if (testNavigation.testletIndex < testPart.AssessmentSectionRefs.length - 1) {
            testNavigation.testletIndex += 1;
        } else {
           let nextTestPartIndex = getNextTestPartIndex(state);
           if (testNavigation.testPartIndex != nextTestPartIndex) {
               testNavigation.testPartIndex = nextTestPartIndex;
               testNavigation.testletIndex = 0;
           }
        }
    }
    return testNavigation;
}

function getNextTestPartIndex(state: ITestTakerState): number {
    if (state.assessmentTest) {
        if (state.testNavigation.testPartIndex < state.assessmentTest.TestParts.length - 1) {
            return state.testNavigation.testPartIndex + 1;
        }
    }
    return state.testNavigation.testPartIndex;
}

function testNavigationPrevious(state: ITestTakerState): PgRendererDto.TestNavigation {
    let testNavigation = Object.assign({}, state.testNavigation);
    if (state.assessmentTest) {
        if (testNavigation.testletIndex > 0) {
            testNavigation.testletIndex -= 1;
        } else {
           let previousTestPartIndex = getPreviousTestPartIndex(state);
           if (testNavigation.testPartIndex != previousTestPartIndex) {
               testNavigation.testPartIndex = previousTestPartIndex;
               testNavigation.testletIndex = 0;
           }
        }
    }
    return testNavigation;
}

function getPreviousTestPartIndex(state: ITestTakerState): number {
    if (state.assessmentTest) {
        if (state.testNavigation.testPartIndex > 0) {
            return state.testNavigation.testPartIndex - 1;
        }
    }
    return state.testNavigation.testPartIndex;
}

//properties like DisplayPageNumber and HasMixedPanelType are not provided by the backend
function setExtraTestletProperties(testlet: PgRendererDto.TestletDefinition) {
    let prevAssessmentItem: PgRendererDto.AssessmentItem = null;
    let groupNumber = 0;
    testlet.AssessmentItems.forEach((item, index) => {
        let displayPageNumber = 1;
        let leftPane = item.ItemBody.ItemBodyPanels.find(x => x.CssClass.indexOf("leftpane") >= 0);
        if (leftPane) {
            if (prevAssessmentItem && prevAssessmentItem.DisplayPageNumber) {
                displayPageNumber += prevAssessmentItem.DisplayPageNumber;
            } else {
                groupNumber += 1;
            }
        } else {
            displayPageNumber = null;
            groupNumber += 1;
        }
        item.ItemIndex = index;
        item.DisplayPageNumber = displayPageNumber;
        item.GroupNumber = groupNumber;
        prevAssessmentItem = item;
    });

    //for groups with only 1 AssessmentItem, set the item's DisplayPageNumber to null
    let groupNumbers = _.uniq(testlet.AssessmentItems.map(x => x.GroupNumber)).forEach(groupNumber => {
        let groupNumberAssessmentItems = testlet.AssessmentItems.filter(x => x.GroupNumber == groupNumber);
        if (groupNumberAssessmentItems.length == 1) {
            groupNumberAssessmentItems[0].DisplayPageNumber = null;
        }
    });

    testlet.HasMixedPanelType = _.uniq(testlet.AssessmentItems.map(item => item.ItemBody.ItemBodyPanels.length)).length > 1;
}





// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/store/reducers/test-taker.reducer.ts